import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Package, Layers, ImageIcon, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { SkuInput } from '@components/SkuInput';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Tabs } from '@components/Tabs';
import { productService } from '@services/productService';
import { productCategoryService } from '@services/productCategoryService';
import { productClassificationService } from '@services/productClassificationService';
import { CreateProductRequest, ProductCategory } from '@/types/product';
import { ProductType, ProductOrigin } from '@/types/productClassification';

/**
 * Formatação do NCM conforme padrão XXXX.XX.XX 
 */
const formatNCM = (value: string): string => {
  // Remove caracteres não numéricos
  const clean = value.replace(/\D/g, '');
  
  // Aplica máscara XXXX.XX.XX
  if (clean.length <= 4) return clean;
  if (clean.length <= 6) return `${clean.slice(0,4)}.${clean.slice(4)}`;
  return `${clean.slice(0,4)}.${clean.slice(4,6)}.${clean.slice(6,8)}`;
};

/**
 * Formulário de produto para criação e edição
 * Segue as especificações da API e validações do backend
 */
export const ProductFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const cloneData = (location.state as any)?.cloneData;
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skuValidationPassed, setSkuValidationPassed] = useState(false);
  // Última mensagem da validação de SKU (ex.: "já está em uso por um produto inativo (...)"),
  // usada para não sobrescrever esse motivo real por uma mensagem genérica no submit.
  const [skuValidationMessage, setSkuValidationMessage] = useState<string>('');

  const handleSkuValidationChange = (isValid: boolean, message?: string) => {
    setSkuValidationPassed(isValid);
    setSkuValidationMessage(isValid ? '' : (message ?? ''));
  };
  
  // Dados do formulário
  const [formData, setFormData] = useState<CreateProductRequest>({
    sku: '',
    description: '',
    price: 0,
    width: 0,
    height: 0,
    length: 0,
    total_weight: 0,
    stock_quantity_available: 0,
    min_stock_quantity: 5,
    max_stock_quantity: 100,
    free_shipping: false,
    can_ship_with_another_inside: false,
    is_fragile: false,
    is_active: true, // Produto ativo por padrão
    is_checkout_required: true, // Exige conferência por padrão
    is_visible_on_crm: true, // Visível no CRM por padrão
    exclude_from_stock_analysis: false, // Entra na análise de estoque por padrão
    is_manufactured: false, // Revenda por padrão; fabricação própria é exceção (atelier)
    is_custom_product: false, // Personalizado (projeto aprovado pela cliente) é exceção
    ncm: '',
    origem: '0', // 0=Nacional, 1=Estrangeira - Importação direta, 2=Estrangeira - Adquirida no mercado interno
    product_origin_id: -1,
    product_type_id: -1,
  });

  // Validação do formulário
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Foto do Projeto (produto personalizado) ─────────────────────
  // O blob é privado: o preview vem do proxy como object URL. O upload/remoção
  // acontece DEPOIS do save (precisa do id do produto).
  const [projectPhotoFile, setProjectPhotoFile] = useState<File | null>(null);
  const [projectPhotoPreview, setProjectPhotoPreview] = useState<string | null>(null);
  const [hasExistingPhoto, setHasExistingPhoto] = useState(false);
  const [projectPhotoRemoved, setProjectPhotoRemoved] = useState(false);

  useEffect(() => {
    return () => {
      if (projectPhotoPreview) URL.revokeObjectURL(projectPhotoPreview);
    };
  }, [projectPhotoPreview]);

  // Carrega a foto existente ao editar (via proxy — nunca a URL do blob).
  useEffect(() => {
    let cancelled = false;
    const loadPhoto = async () => {
      if (!isEditing || !id) return;
      try {
        const product = await productService.getProductById(parseInt(id));
        if (cancelled || !product.has_project_photo) return;
        const url = await productService.getProjectPhotoFile(parseInt(id));
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setHasExistingPhoto(true);
        setProjectPhotoPreview(url);
      } catch {
        // Sem foto ou erro de rede: a seção fica vazia.
      }
    };
    loadPhoto();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, id]);

  const handleProjectPhotoSelected = (file: File | null) => {
    if (!file) return;
    if (projectPhotoPreview) URL.revokeObjectURL(projectPhotoPreview);
    setProjectPhotoFile(file);
    setProjectPhotoPreview(URL.createObjectURL(file));
    setProjectPhotoRemoved(false);
  };

  const handleProjectPhotoRemove = () => {
    if (projectPhotoPreview) URL.revokeObjectURL(projectPhotoPreview);
    setProjectPhotoFile(null);
    setProjectPhotoPreview(null);
    setProjectPhotoRemoved(hasExistingPhoto);
  };

  // Listas de classificação fiscal
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [productOrigins, setProductOrigins] = useState<ProductOrigin[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  // Carrega categorias de produto (apenas ativas) para o seletor.
  useEffect(() => {
    productCategoryService
      .listCategories(1, 200, undefined, false)
      .then(({ categories }) => setCategories(categories))
      .catch(() => { /* categoria é opcional */ });
  }, []);

  // Carrega listas de classificação fiscal
  useEffect(() => {
    const loadClassifications = async () => {
      try {
        const [types, origins] = await Promise.all([
          productClassificationService.getProductTypes(),
          productClassificationService.getProductOrigins(),
        ]);
        setProductTypes(types);
        setProductOrigins(origins);
      } catch (err) {
        console.error('Erro ao carregar classificações fiscais:', err);
      }
    };
    loadClassifications();
  }, []);

  // Carrega produto para edição
  useEffect(() => {
    if (isEditing && id) {
      loadProduct(id);
    }
  }, [id, isEditing]);

  // Se veio cloneData via navigation state e não é edição, preenche o formulário
  useEffect(() => {
    if (!isEditing && cloneData) {
      setFormData({
        sku: '', // limpar SKU para evitar duplicidade
        description: cloneData.description || '',
        price: cloneData.price || 0,
        width: cloneData.width || 0,
        height: cloneData.height || 0,
        length: cloneData.length || 0,
        total_weight: cloneData.total_weight || 0,
        stock_quantity_available: cloneData.stock_quantity_available || 0,
        min_stock_quantity: cloneData.min_stock_quantity || 5,
        max_stock_quantity: cloneData.max_stock_quantity || 100,
        free_shipping: cloneData.free_shipping || false,
        can_ship_with_another_inside: cloneData.can_ship_with_another_inside === true,
        is_fragile: cloneData.is_fragile || false,
        is_active: cloneData.is_active !== false,
        is_checkout_required: cloneData.is_checkout_required !== false,
        is_visible_on_crm: cloneData.is_visible_on_crm !== false,
        exclude_from_stock_analysis: cloneData.exclude_from_stock_analysis === true,
        is_manufactured: cloneData.is_manufactured === true,
        is_custom_product: cloneData.is_custom_product === true,
        ncm: formatNCM(cloneData.ncm || ''),
        origem: cloneData.origem || '0',
        category_id: cloneData.category_id ?? null,
        product_origin_id: cloneData.product_origin_id ?? -1,
        product_type_id: cloneData.product_type_id ?? -1,
      });
      setSkuValidationPassed(false);
    }
  }, [cloneData, isEditing]);

  const loadProduct = async (productId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const product = await productService.getProductById(parseInt(productId));
      
      // Mapeia os dados do produto para o formulário
      setFormData({
        sku: product.sku,
        description: product.description,
        price: product.price || 0,
        width: product.width || 0,
        height: product.height || 0,
        length: product.length || 0,
        total_weight: product.total_weight || 0,
        stock_quantity_available: product.stock_quantity_available || 0,
        min_stock_quantity: product.min_stock_quantity || 5,
        max_stock_quantity: product.max_stock_quantity || 100,
        free_shipping: product.free_shipping || false,
        can_ship_with_another_inside: product.can_ship_with_another_inside === true, // NULL no banco = não permite (só caixas próprias para isso)
        is_fragile: product.is_fragile || false,
        is_active: product.is_active !== false, // Ativo por padrão se não especificado
        is_checkout_required: product.is_checkout_required !== false, // Exige conferência por padrão
        is_visible_on_crm: product.is_visible_on_crm !== false, // Visível no CRM por padrão
        exclude_from_stock_analysis: product.exclude_from_stock_analysis === true, // Entra na análise por padrão
        is_manufactured: product.is_manufactured === true, // Revenda por padrão
        is_custom_product: product.is_custom_product === true,
        ncm: product.ncm || '',
        origem: product.origem || '0',
        category_id: product.category_id ?? null,
        product_origin_id: product.product_origin_id ?? -1,
        product_type_id: product.product_type_id ?? -1,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  };

  // Atualiza campo do formulário
  const updateField = (field: keyof CreateProductRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Remove erro do campo quando o usuário começa a digitar
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validação dos campos
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU é obrigatório';
    } else if (formData.sku.length > 50) {
      newErrors.sku = 'SKU deve ter no máximo 50 caracteres';
    } else if (!skuValidationPassed && !isEditing) {
      // Durante criação, o SKU deve ter passado na validação. Se houver um motivo
      // concreto (ex.: SKU em uso por produto inativo), mostra-o em vez da mensagem
      // genérica de "aguarde".
      newErrors.sku = skuValidationMessage || 'Por favor, aguarde a validação do SKU';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Descrição deve ter no máximo 500 caracteres';
    }
    
    if ((formData.price || 0) <= 0) {
      newErrors.price = 'Preço deve ser maior que zero';
    }
    
    if ((formData.min_stock_quantity || 0) < 0) {
      newErrors.min_stock_quantity = 'Estoque mínimo não pode ser negativo';
    }
    
    if ((formData.max_stock_quantity || 0) <= 0) {
      newErrors.max_stock_quantity = 'Estoque máximo deve ser maior que zero';
    }
    
    if ((formData.min_stock_quantity || 0) > (formData.max_stock_quantity || 0)) {
      newErrors.min_stock_quantity = 'Estoque mínimo deve ser menor que o máximo';
    }
    
    if ((formData.width || 0) < 0 || (formData.height || 0) < 0 || (formData.length || 0) < 0) {
      newErrors.dimensions = 'Dimensões devem ser números inteiros não negativos';
    }
    
    // Validação adicional para garantir que são inteiros
    if (formData.width && !Number.isInteger(formData.width)) {
      newErrors.width = 'Largura deve ser um número inteiro';
    }
    if (formData.height && !Number.isInteger(formData.height)) {
      newErrors.height = 'Altura deve ser um número inteiro';
    }
    if (formData.length && !Number.isInteger(formData.length)) {
      newErrors.length = 'Comprimento deve ser um número inteiro';
    }
    
    if ((formData.total_weight || 0) < 0) {
      newErrors.total_weight = 'Peso não pode ser negativo';
    }
    
    if (formData.ncm && !/^\d{4}\.\d{2}\.\d{2}$/.test(formData.ncm)) {
      newErrors.ncm = 'NCM deve seguir o formato XXXX.XX.XX (ex: 4202.11.00)';
    }

    if (!formData.ncm || !formData.ncm.trim()) {
      newErrors.ncm = 'NCM é obrigatório';
    }

    if (formData.product_origin_id < 0) {
      newErrors.product_origin_id = 'Origem do produto é obrigatória';
    }

    if (formData.product_type_id < 0) {
      newErrors.product_type_id = 'Tipo do produto é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salva o produto
  const handleSave = async () => {

    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      console.log('🔄 Starting save operation...');
      
      let savedProductId: number;
      if (isEditing && id) {
        const { stock_quantity_available, ...updateData } = formData;

        console.log('📝 Updating existing product with ID:', id);
        console.log('📝 Update data:', updateData);
        console.log('ℹ️ stock_quantity_available removido do payload de edição:', stock_quantity_available);
        const result = await productService.updateProduct(parseInt(id), updateData);
        console.log('✅ Product updated successfully:', result);
        savedProductId = parseInt(id);
      } else {
        console.log('➕ Creating new product');
        const result = await productService.createProduct(formData);
        console.log('✅ Product created successfully:', result);
        savedProductId = result.id;
      }

      // Foto do projeto: upload/remoção após o save (precisa do id). Falha da
      // foto não desfaz o save — avisa e mantém o usuário na tela.
      try {
        if (formData.is_custom_product && projectPhotoFile) {
          await productService.uploadProjectPhoto(savedProductId, projectPhotoFile);
        } else if (projectPhotoRemoved) {
          await productService.deleteProjectPhoto(savedProductId);
        }
      } catch (photoErr) {
        console.error('❌ Error saving project photo:', photoErr);
        setError(photoErr instanceof Error ? photoErr.message : 'Produto salvo, mas houve erro ao salvar a foto do projeto');
        return;
      }
      
      console.log('🔄 Navigating back to products list');
      navigate('/products');
    } catch (err) {
      console.error('❌ Error saving product:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar produto';
      
      // Se for erro de SKU duplicado, foca no campo SKU
      if (errorMessage.toLowerCase().includes('sku') && errorMessage.toLowerCase().includes('uso')) {
        // Adiciona erro específico ao campo SKU
        setErrors(prev => ({ ...prev, sku: errorMessage }));
        
        // Foca no campo SKU após um pequeno delay
        setTimeout(() => {
          const skuInput = document.querySelector('input[placeholder*="ABC123"]') as HTMLInputElement;
          if (skuInput) {
            skuInput.focus();
            skuInput.select();
          }
        }, 100);
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
      console.log('🔄 Save operation finished');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <div className="grid gap-6">
          <Card>
            <CardContent>
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/products')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Produto' : 'Novo Produto'}
          </h1>
          <p className="text-slate-600">
            {isEditing 
              ? 'Altere as informações do produto'
              : 'Preencha os dados para cadastrar um novo produto'
            }
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário com Abas */}
      <Tabs
        tabs={[
          {
            id: 'general',
            label: 'Informações Gerais',
            icon: <Package size={16} />,
            content: (
              <div className="grid gap-6">
                {/* Informações básicas */}
                <Card>
                  <CardHeader title="Informações Básicas" />
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SkuInput
                        value={formData.sku}
                        onChange={(value) => updateField('sku', value)}
                        error={errors.sku}
                        onValidationChange={handleSkuValidationChange}
                        isEditing={isEditing}
                        // O SKU identifica o produto em pedidos, kits, site e CRM: a API recusa a troca.
                        disabled={isEditing}
                        label={isEditing ? 'SKU (não pode ser alterado)' : undefined}
                        maxLength={50}
                      />
                      <div className="md:col-span-2">
                        <Input
                          label="Descrição *"
                          placeholder="Ex: Smartphone Android 128GB"
                          value={formData.description}
                          onChange={(e) => updateField('description', e.target.value)}
                          error={errors.description}
                          maxLength={500}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Categoria</label>
                        <select
                          value={formData.category_id ?? ''}
                          onChange={(e) =>
                            updateField('category_id', e.target.value ? Number(e.target.value) : null)
                          }
                          className="select-field"
                        >
                          <option value="">— Sem categoria —</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dados Fiscais */}
                <Card>
                  <CardHeader title="Dados Fiscais" />
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="NCM *"
                        placeholder="Ex: 4202.11.00"
                        value={formData.ncm}
                        onChange={(e) => updateField('ncm', formatNCM(e.target.value))}
                        error={errors.ncm}
                        maxLength={10}
                      />
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Tipo do Produto (SPED) *</label>
                        <select
                          value={String(formData.product_type_id)}
                          onChange={(e) => updateField('product_type_id', Number(e.target.value))}
                          className={`select-field ${errors.product_type_id ? 'border-red-300' : ''}`}
                        >
                          <option value="-1">Selecione</option>
                          {productTypes.map((pt) => (
                            <option key={pt.product_type_id} value={String(pt.product_type_id)}>
                              {pt.product_type_description}
                            </option>
                          ))}
                        </select>
                        {errors.product_type_id && (
                          <p className="text-sm text-red-600 mt-1">{errors.product_type_id}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Origem do Produto *</label>
                        <select
                          value={String(formData.product_origin_id)}
                          onChange={(e) => updateField('product_origin_id', Number(e.target.value))}
                          className={`select-field ${errors.product_origin_id ? 'border-red-300' : ''}`}
                        >
                          <option value="-1">Selecione</option>
                          {productOrigins.map((po) => (
                            <option key={po.product_origin_id} value={String(po.product_origin_id)}>
                              {po.product_origin_description}
                            </option>
                          ))}
                        </select>
                        {errors.product_origin_id && (
                          <p className="text-sm text-red-600 mt-1">{errors.product_origin_id}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Valores */}
                <Card>
                  <CardHeader title="Preços e Custos" />
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Preço de Venda *"
                        type="number"
                        placeholder="0,00"
                        value={formData.price}
                        onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
                        error={errors.price}
                        step={0.01}
                        min={0}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Dimensões e peso */}
                <Card>
                  <CardHeader title="Dimensões e Peso" />
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Input label="Largura (cm)" type="number" placeholder="0" value={formData.width} onChange={(e) => updateField('width', parseInt(e.target.value) || 0)} step={1} min={0} error={errors.width || errors.dimensions} />
                      <Input label="Altura (cm)" type="number" placeholder="0" value={formData.height} onChange={(e) => updateField('height', parseInt(e.target.value) || 0)} step={1} min={0} error={errors.height || errors.dimensions} />
                      <Input label="Comprimento (cm)" type="number" placeholder="0" value={formData.length} onChange={(e) => updateField('length', parseInt(e.target.value) || 0)} step={1} min={0} error={errors.length || errors.dimensions} />
                      <Input label="Peso (kg)" type="number" placeholder="0,000" value={formData.total_weight} onChange={(e) => updateField('total_weight', parseFloat(e.target.value) || 0)} step={0.001} min={0} error={errors.total_weight} />
                    </div>
                    {errors.dimensions && <p className="text-sm text-red-600 mt-2">{errors.dimensions}</p>}
                  </CardContent>
                </Card>

                {/* Configurações de envio */}
                <Card>
                  <CardHeader title="Configurações de Envio" />
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="free_shipping" checked={formData.free_shipping} onChange={(e) => updateField('free_shipping', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="free_shipping" className="text-sm text-slate-700">Frete grátis</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="can_ship_with_another" checked={formData.can_ship_with_another_inside} onChange={(e) => updateField('can_ship_with_another_inside', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="can_ship_with_another" className="text-sm text-slate-700">Pode ser enviado com outros produtos dentro dele</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="is_fragile" checked={formData.is_fragile} onChange={(e) => updateField('is_fragile', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="is_fragile" className="text-sm text-slate-700">Produto frágil</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => updateField('is_active', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="is_active" className="text-sm text-slate-700">Produto ativo</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="is_checkout_required" checked={formData.is_checkout_required} onChange={(e) => updateField('is_checkout_required', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="is_checkout_required" className="text-sm text-slate-700">Exige conferência no checkout</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" id="is_visible_on_crm" checked={formData.is_visible_on_crm} onChange={(e) => updateField('is_visible_on_crm', e.target.checked)} className="rounded border-slate-300" />
                        <label htmlFor="is_visible_on_crm" className="text-sm text-slate-700">Visível no CRM</label>
                      </div>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" id="is_manufactured" checked={formData.is_manufactured} onChange={(e) => updateField('is_manufactured', e.target.checked)} className="rounded border-slate-300 mt-0.5" />
                        <label htmlFor="is_manufactured" className="text-sm text-slate-700">
                          Fabricação própria
                          <span className="block text-xs text-slate-500">
                            Produto fabricado no atelier (cabides flocados, presilhas, ombreiras, tira vinco).
                            Independe da classificação fiscal e deve ter Lista de Materiais no módulo de Produção.
                          </span>
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <input type="checkbox" id="is_custom_product" checked={formData.is_custom_product ?? false} onChange={(e) => updateField('is_custom_product', e.target.checked)} className="rounded border-slate-300 mt-0.5" />
                        <label htmlFor="is_custom_product" className="text-sm text-slate-700">
                          Produto personalizado
                          <span className="block text-xs text-slate-500">
                            O projeto/arte é aprovado pela cliente. Habilita a Foto do Projeto abaixo,
                            exibida no pedido de venda e no recebimento de materiais, e as fotos de
                            conferência do recebimento pela logística.
                          </span>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {(formData.is_custom_product ?? false) && (
                  <Card>
                    <CardHeader title="Foto do Projeto" />
                    <CardContent>
                      <p className="text-xs text-slate-500 mb-3">
                        Foto do projeto/especificação aprovada pela cliente. Aparece no pedido de venda e
                        no recebimento de materiais para conferência do que foi entregue.
                      </p>
                      <div className="flex items-start gap-4">
                        {projectPhotoPreview ? (
                          <div className="relative">
                            <img
                              src={projectPhotoPreview}
                              alt="Foto do projeto"
                              className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={handleProjectPhotoRemove}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              title="Remover foto"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <label className="w-32 h-32 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 cursor-pointer hover:border-slate-400 hover:text-slate-500">
                            <ImageIcon size={24} />
                            <span className="text-xs">Enviar foto</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleProjectPhotoSelected(e.target.files?.[0] ?? null)}
                            />
                          </label>
                        )}
                        {projectPhotoPreview && (
                          <label className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer mt-1">
                            Trocar foto
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleProjectPhotoSelected(e.target.files?.[0] ?? null)}
                            />
                          </label>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ),
          },
          {
            id: 'stock',
            label: 'Controle de Estoque',
            icon: <Layers size={16} />,
            content: (
              <div className="grid gap-6">
                <Card>
                  <CardHeader title="Controle de Estoque" />
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Estoque Disponível</label>
                        <div className="px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-600">
                          {formData.stock_quantity_available ?? 0}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Atualizado por lançamentos de estoque</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Estoque Reservado</label>
                        <div className="px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-600">0</div>
                        <p className="text-xs text-slate-500 mt-1">Calculado automaticamente pela API</p>
                      </div>
                      <Input
                        label="Estoque Mínimo *"
                        type="number"
                        placeholder="5"
                        value={formData.min_stock_quantity}
                        onChange={(e) => updateField('min_stock_quantity', parseInt(e.target.value) || 0)}
                        error={errors.min_stock_quantity}
                        min={0}
                      />
                      <Input
                        label="Estoque Máximo *"
                        type="number"
                        placeholder="100"
                        value={formData.max_stock_quantity}
                        onChange={(e) => updateField('max_stock_quantity', parseInt(e.target.value) || 0)}
                        error={errors.max_stock_quantity}
                        min={1}
                      />
                    </div>
                    <div className="mt-4 flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="exclude_from_stock_analysis"
                        checked={formData.exclude_from_stock_analysis}
                        onChange={(e) => updateField('exclude_from_stock_analysis', e.target.checked)}
                        className="rounded border-slate-300 mt-0.5"
                      />
                      <label htmlFor="exclude_from_stock_analysis" className="text-sm text-slate-700">
                        Fora da análise de estoque
                        <span className="block text-xs text-slate-500">
                          Marque para produtos que não devem entrar na Análise de Estoque / Planejamento de Compras
                          (ex.: outlet com avaria, cabides e iniciais fabricados sob demanda).
                        </span>
                      </label>
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      <p className="font-medium mb-1">ℹ️ Informação:</p>
                      <p>O estoque disponível e reservado são gerenciados automaticamente por lançamentos de estoque (conferência de estoque, recebimento de materiais e pedidos de venda). Para ajustar o estoque, utilize a tela de Conferência de Estoque.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ),
          },
        ]}
        defaultTab="general"
      />

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/products')}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          icon={<Save size={20} />}
        >
          {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Produto'}
        </Button>
      </div>
    </div>
  );
};